// ---------------------------------------------------------------------------
// Puck Component Catalog for AI Context
// ---------------------------------------------------------------------------
// The AI layer must use the same canonical block contract as the editor and
// SSR renderer. Derive the catalog directly from the shared block registry so
// prompt context cannot drift from runtime reality.

import {
  SHARED_BLOCK_REGISTRY,
  type BlockDefinition,
  type PropDef as RegistryPropDef,
} from "../block-registry.js";
import { COMPONENT_TYPES, type ComponentType } from "../constants.js";
import type { PuckData, PuckComponent } from "./puck-types.js";

// ---------------------------------------------------------------------------
// Prop definitions
// ---------------------------------------------------------------------------

export type PropDef = RegistryPropDef;

export interface ComponentDef {
  type: ComponentType;
  label: string;
  description: string;
  usageGuidance: string;
  props: readonly PropDef[];
  hasZones?: boolean;
  zoneNames?: readonly string[];
}

function toComponentDef(block: BlockDefinition): ComponentDef {
  return {
    type: block.type as ComponentType,
    label: block.label,
    description: block.description,
    usageGuidance: block.usageGuidance,
    props: block.props,
    hasZones: block.hasZones,
    zoneNames: block.zoneNames,
  };
}

// ---------------------------------------------------------------------------
// Component catalog
// ---------------------------------------------------------------------------

export const COMPONENT_CATALOG: readonly ComponentDef[] = SHARED_BLOCK_REGISTRY.map(
  toComponentDef,
);

const COMPONENT_CATALOG_MAP = new Map<ComponentType, ComponentDef>(
  COMPONENT_CATALOG.map((component) => [component.type, component]),
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getComponentDef(type: ComponentType): ComponentDef | undefined {
  return COMPONENT_CATALOG_MAP.get(type);
}

export function serializeComponentCatalog(): string {
  const lines: string[] = ["## Available Components\n"];

  for (const comp of COMPONENT_CATALOG) {
    lines.push(`### ${comp.label} (type: "${comp.type}")`);
    lines.push(comp.description);
    lines.push(`Usage: ${comp.usageGuidance}`);
    lines.push("Props:");

    for (const prop of comp.props) {
      const req = prop.required ? "REQUIRED" : "optional";
      const def = prop.defaultValue ? ` (default: ${prop.defaultValue})` : "";
      lines.push(`  - ${prop.name}: ${prop.type} [${req}]${def} — ${prop.description}`);
    }

    lines.push("");
  }

  return lines.join("\n");
}

export function getMissingComponents(): ComponentType[] {
  const catalogTypes = new Set(COMPONENT_CATALOG.map((c) => c.type));
  return COMPONENT_TYPES.filter((t) => !catalogTypes.has(t));
}

function summarizePropValue(value: unknown): string {
  if (typeof value === "string") {
    const normalized = value.replace(/\s+/g, " ").trim();
    return normalized.length > 60 ? `${normalized.slice(0, 57)}...` : normalized;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return `[${value.length} item${value.length === 1 ? "" : "s"}]`;
  }

  if (value && typeof value === "object") {
    const keys = Object.keys(value);
    return `{${keys.slice(0, 3).join(", ")}${keys.length > 3 ? ", ..." : ""}}`;
  }

  return String(value);
}

function describeComponent(component: PuckComponent, fallbackIndex: number): string {
  const componentId =
    typeof component.props.id === "string" && component.props.id.length > 0
      ? component.props.id
      : component._id ?? `${component.type}-${fallbackIndex + 1}`;
  const definition = getComponentDef(component.type as ComponentType);
  const knownProps = definition?.props.map((prop) => prop.name) ?? [];
  const presentProps = knownProps.filter((prop) => prop in component.props && prop !== "id");
  const propSummary = presentProps.length > 0
    ? presentProps
        .slice(0, 5)
        .map((prop) => `${prop}=${summarizePropValue(component.props[prop])}`)
        .join(", ")
    : "no canonical props set";
  const label = definition?.label ?? component.type;

  return `- ${componentId} | ${component.type} | ${label} | ${propSummary}`;
}

export function serializeBuilderStateForPrompt(puckData: PuckData): string {
  const lines = ["## Current Builder Blocks"];

  puckData.content.forEach((component, index) => {
    lines.push(describeComponent(component, index));
  });

  if (puckData.zones) {
    const zoneEntries = Object.entries(puckData.zones);
    for (const [zoneKey, zoneComponents] of zoneEntries) {
      lines.push(`Zone ${zoneKey}:`);
      zoneComponents.forEach((component, index) => {
        lines.push(`  ${describeComponent(component, index)}`);
      });
    }
  }

  return lines.join("\n");
}
